// Shared per-coin order-whales poller. Was: every widget instance had
// its own setInterval + fetch. Now: one poller per coin, regardless of
// how many widgets are watching it. Refcount drives start/stop.

import { API_BASE_URL } from '../../../utils/apiConfig';
import { createSharedStore } from './createSharedStore';

const POLL_INTERVAL = 60_000;
const FETCH_TIMEOUT_MS = 10_000;

const timers = new Map();    // coin -> intervalId
const inflight = new Map();  // coin -> AbortController

async function fetchOnce(coin, storeApi) {
  const controller = new AbortController();
  inflight.get(coin)?.abort();
  inflight.set(coin, controller);

  // Skip when hidden — comes back via visibilitychange in the page tab and
  // the timer keeps firing, so we noop here and wait for next interval.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }

  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE_URL}/api/order-whales/${coin}`, { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data?.success) {
      storeApi.set(coin, {
        levels: data.levels || [],
        loading: false,
        error: null,
      });
    } else {
      storeApi.set(coin, { ...storeApi.get(coin), loading: false, error: data?.error || 'failed' });
    }
  } catch (err) {
    clearTimeout(timeout);
    if (err?.name === 'AbortError') return;
    storeApi.set(coin, { ...storeApi.get(coin), loading: false, error: err.message });
  } finally {
    if (inflight.get(coin) === controller) inflight.delete(coin);
  }
}

export const sharedOrderWhales = createSharedStore({
  name: 'orderWhales',
  emptyValue: () => ({ levels: [], loading: true, error: null }),
  startStream(coin, storeApi) {
    fetchOnce(coin, storeApi).catch(() => {});
    const id = setInterval(() => { fetchOnce(coin, storeApi).catch(() => {}); }, POLL_INTERVAL);
    timers.set(coin, id);
  },
  stopStream(coin) {
    const id = timers.get(coin);
    if (id) { clearInterval(id); timers.delete(coin); }
    inflight.get(coin)?.abort();
    inflight.delete(coin);
  },
});

export function useSharedOrderWhales(coin) {
  return sharedOrderWhales.useShared(coin);
}
